<template>
  <div class="p-8" :class="{ 'pb-24': configRequest?.status === 1 }">
    <nav class="mb-4">
      <router-link :to="{ name: 'admin.config-requests' }" class="text-primary-600 dark:text-primary-400 hover:underline">
        Config Requests
      </router-link>
      <span class="mx-2 text-surface-400">/</span>
      <span class="text-surface-600 dark:text-surface-400">{{ configRequest?.title || 'Loading...' }}</span>
    </nav>
    <h1 class="text-2xl font-bold mb-6">{{ configRequest?.title || 'Loading...' }}</h1>

    <div v-if="!loading && configRequest">
      <!-- Request Info and Description Cards -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <!-- Request Info Card -->
        <div class="bg-white dark:bg-surface-800 rounded-lg shadow p-6">
          <h2 class="text-lg font-semibold mb-3">Request Information</h2>
          <div class="space-y-2">
            <div>
              <span class="text-sm font-medium text-surface-600 dark:text-surface-400">Status:</span>
              <div class="mt-1">
                <Tag :value="getStatusLabel(configRequest.status)" :severity="getStatusSeverity(configRequest.status)" />
              </div>
            </div>
            <div>
              <span class="text-sm font-medium text-surface-600 dark:text-surface-400">Requested By:</span>
              <p class="text-surface-900 dark:text-surface-50">{{ configRequest.requested_by?.name || '-' }}</p>
            </div>
            <div>
              <span class="text-sm font-medium text-surface-600 dark:text-surface-400">Requested At:</span>
              <p class="text-surface-900 dark:text-surface-50">{{ formatDate(configRequest.created_at) }}</p>
            </div>
            <div>
              <span class="text-sm font-medium text-surface-600 dark:text-surface-400">Updated At:</span>
              <p class="text-surface-900 dark:text-surface-50">{{ formatDate(configRequest.updated_at) }}</p>
            </div>
          </div>
        </div>

        <!-- Description Card -->
        <div v-if="configRequest.comment || configRequest.rejected_reason" class="bg-white dark:bg-surface-800 rounded-lg shadow p-6">
          <div v-if="configRequest.comment" class="mb-4">
            <h2 class="text-lg font-semibold mb-3">Description</h2>
            <p class="text-surface-700 dark:text-surface-300">{{ configRequest.comment }}</p>
          </div>
          <div v-if="configRequest.status === 3 && configRequest.rejected_reason">
            <h2 class="text-lg font-semibold mb-3">Rejection Reason</h2>
            <p class="text-surface-700 dark:text-surface-300">{{ configRequest.rejected_reason }}</p>
          </div>
        </div>
      </div>

      <!-- Configuration Changes (Diff View) -->
      <div class="bg-white dark:bg-surface-800 rounded-lg shadow p-6">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-lg font-semibold">Configuration Changes</h2>
          <div class="flex gap-2">
            <Button
              label="View Original"
              icon="pi pi-external-link"
              text
              size="small"
              :disabled="!hasCurrentConfig"
              @click="openConfigInNewWindow('original')"
            />
            <Button
              label="View New Config"
              icon="pi pi-external-link"
              text
              size="small"
              @click="openConfigInNewWindow('new')"
            />
            <Button
              :label="isExpanded ? 'Collapse' : 'Expand'"
              :icon="isExpanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'"
              text
              size="small"
              @click="isExpanded = !isExpanded"
            />
          </div>
        </div>

        <div v-if="!hasCurrentConfig" class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 mb-4">
          <div class="flex items-center gap-3">
            <i class="pi pi-exclamation-triangle text-amber-500 text-xl"></i>
            <div>
              <p class="font-medium text-amber-800 dark:text-amber-200 mb-4">No current configuration found</p>
              <p class="text-sm text-amber-700 dark:text-amber-300">
                There is no current configuration record (status: Current) for this device. Unable to show configuration changes.<br />
                Please reconnect the device to Diag++ and connect to SUMS to upload the existing config.
              </p>
            </div>
          </div>
        </div>

        <div :class="['overflow-auto', isExpanded ? '' : 'max-h-[400px]']">
          <div v-for="(section, index) in configDiffData" :key="index" :class="{ 'mt-6': index > 0 }">
            <h3
              class="text-lg font-semibold text-surface-900 dark:text-surface-50 mb-2 bg-surface-100 dark:bg-surface-700 p-2 rounded text-center cursor-pointer flex items-center justify-center gap-2"
              @click="toggleSection(index)"
            >
              <span>{{ section.title }}</span>
              <span class="text-sm font-normal text-surface-500">({{ section.rows.length }} changes)</span>
              <i :class="collapsedSections[index] ? 'pi pi-chevron-down' : 'pi pi-chevron-up'" class="text-sm"></i>
            </h3>
            <div v-if="!collapsedSections[index]" class="w-full mx-auto mb-4" style="max-width: 90%;">
              <DataTable
                :value="section.rows"
                size="small"
                class="border border-surface-200 dark:border-surface-700"
                :scrollable="false"
              >
                <Column field="key" header="Key" class="font-medium" style="width: 30%" />
                <Column header="Current Value" style="width: 35%">
                  <template #body="slotProps">
                    <span :class="{ 'text-surface-400 italic': slotProps.data.isNew }">
                      {{ slotProps.data.isNew ? '(new key)' : slotProps.data.currentValue }}
                    </span>
                  </template>
                </Column>
                <Column header="New Value" style="width: 35%">
                  <template #body="slotProps">
                    <span :class="{ 'text-red-500 line-through': slotProps.data.isRemoved, 'text-green-600 dark:text-green-400 font-medium': !slotProps.data.isRemoved }">
                      {{ slotProps.data.isRemoved ? '(removed)' : slotProps.data.newValue }}
                    </span>
                  </template>
                </Column>
              </DataTable>
            </div>
          </div>
          <div v-if="configDiffData.length === 0 && hasCurrentConfig" class="text-center text-surface-500 dark:text-surface-400 py-8">
            No configuration changes detected.<br /><br /> The requested configuration is identical to the current configuration.
          </div>
        </div>
      </div>

      <!-- Signature Card -->
      <div class="bg-white dark:bg-surface-800 rounded-lg shadow p-6 mt-6 mb-12">
        <h2 class="text-lg font-semibold mb-3">Signature (Binary) <span v-if="signatureHex" class="text-sm font-normal text-surface-500">({{ signatureByteLength }} bytes)</span></h2>
        <div v-if="signatureBinary" class="font-mono text-sm text-surface-700 dark:text-surface-300 break-all">
          {{ signatureBinary }}
        </div>
        <div v-else class="text-surface-500 dark:text-surface-400 italic">
          Blank - approve this request to generate
        </div>

        <h2 class="text-lg font-semibold mb-3 mt-6">Signature (Hex) <span v-if="signatureHex" class="text-sm font-normal text-surface-500">({{ signatureHex.length }} characters)</span></h2>
        <div v-if="signatureHex" class="font-mono text-sm text-surface-700 dark:text-surface-300 break-all">
          {{ signatureHex }}
        </div>
        <div v-else class="text-surface-500 dark:text-surface-400 italic">
          Blank - approve this request to generate
        </div>

        <h2 class="text-lg font-semibold mb-3 mt-6">Hash <span v-if="configRequest.hash" class="text-sm font-normal text-surface-500">({{ configRequest.hash.length }} characters)</span></h2>
        <div v-if="configRequest.hash" class="font-mono text-sm text-surface-700 dark:text-surface-300 break-all">
          {{ configRequest.hash }}
        </div>
        <div v-else class="text-surface-500 dark:text-surface-400 italic">
          Blank - approve this request to generate
        </div>
      </div>
    </div>

    <div v-if="loading" class="text-surface-500 dark:text-surface-400">
      Loading...
    </div>

    <div v-if="notFound" class="text-center py-12">
      <i class="pi pi-exclamation-circle text-4xl text-surface-400 mb-4"></i>
      <p class="text-lg text-surface-600 dark:text-surface-400">Config change {{ $route.params.id }} cannot be found.</p>
      <router-link :to="{ name: 'admin.config-requests' }" class="text-primary-600 dark:text-primary-400 hover:underline mt-4 inline-block">
        Back to Config Requests
      </router-link>
    </div>

    <!-- Sticky Action Bar -->
    <div v-if="!loading && configRequest" class="fixed bottom-0 left-0 right-0 lg:left-72 bg-white dark:bg-surface-800 border-t border-surface-200 dark:border-surface-700 p-4 shadow-lg z-10">
      <div class="flex justify-end gap-3">
        <Button
          label="Reject"
          icon="pi pi-times"
          severity="danger"
          :disabled="configRequest.status === 3 || configRequest.status === 4"
          @click="showRejectModal = true"
        />
        <Button
          label="Approve"
          icon="pi pi-check"
          severity="success"
          :disabled="configRequest.status === 2 || configRequest.status === 4"
          @click="showApproveModal = true"
        />
      </div>
    </div>

    <!-- Approve Confirmation Modal -->
    <Dialog
      v-model:visible="showApproveModal"
      header="Approve Config Change Request"
      :modal="true"
      :closable="true"
      :style="{ width: '450px' }"
    >
      <p class="mb-4">Are you sure you want to approve this config change request?</p>
      <template #footer>
        <Button label="Cancel" text @click="showApproveModal = false" />
        <Button label="Approve" severity="success" @click="approveRequest" :loading="submitting" />
      </template>
    </Dialog>

    <!-- Reject Modal -->
    <Dialog
      v-model:visible="showRejectModal"
      header="Reject Config Change Request"
      :modal="true"
      :closable="true"
      :style="{ width: '500px' }"
    >
      <div class="mb-4">
        <label for="rejection_reason" class="block text-sm font-medium mb-2">Rejection Reason *</label>
        <Textarea
          id="rejection_reason"
          v-model="rejectionReason"
          rows="5"
          class="w-full"
          placeholder="Please provide a reason for rejecting this request..."
        />
      </div>
      <template #footer>
        <Button label="Cancel" text @click="showRejectModal = false" />
        <Button label="Reject" severity="danger" @click="rejectRequest" :loading="submitting" :disabled="!rejectionReason" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { useConfigChangeRequestsStore } from "@/stores/configChangeRequests.js";
import { mapStores } from "pinia";
import dayjs from 'dayjs';

export default {
  name: 'ConfigRequestShow',
  data() {
    return {
      configRequest: null,
      loading: false,
      notFound: false,
      isExpanded: false,
      showApproveModal: false,
      showRejectModal: false,
      rejectionReason: '',
      submitting: false,
      collapsedSections: {},
    }
  },
  computed: {
    ...mapStores(useConfigChangeRequestsStore),
    signatureHex() {
      return this.configRequest?.signature || null;
    },
    signatureBinary() {
      if (!this.signatureHex) {
        return null;
      }
      // Convert hex string to binary representation
      const bytes = [];
      for (let i = 0; i < this.signatureHex.length; i += 2) {
        bytes.push(String.fromCharCode(parseInt(this.signatureHex.substr(i, 2), 16)));
      }
      return bytes.join('');
    },
    signatureByteLength() {
      return this.signatureHex ? this.signatureHex.length / 2 : 0;
    },
    hashHex() {
      return this.configRequest?.hash || null;
    },
    hasCurrentConfig() {
      return !!this.configRequest?.current_config;
    },
    parsedCurrentConfig() {
      return this.parseConfigToMap(this.configRequest?.current_config);
    },
    parsedRequestedConfig() {
      return this.parseConfigToMap(this.configRequest?.requested_config);
    },
    configDiffData() {
      const requestedMap = this.parsedRequestedConfig;
      const currentMap = this.parsedCurrentConfig;

      // If no current config, show all requested values as new
      if (!this.hasCurrentConfig) {
        return this.convertMapToSections(requestedMap, null);
      }

      // Build diff: only show changed values
      const diffSections = [];

      // Get all section names from both configs
      const allSections = new Set([...Object.keys(requestedMap), ...Object.keys(currentMap)]);

      allSections.forEach(sectionName => {
        const currentSection = currentMap[sectionName] || {};
        const requestedSection = requestedMap[sectionName] || {};
        const diffRows = [];

        // Get all keys from both sections
        const allKeys = new Set([...Object.keys(currentSection), ...Object.keys(requestedSection)]);

        allKeys.forEach(key => {
          const currentValue = currentSection[key];
          const newValue = requestedSection[key];

          // Only include if values are different
          if (currentValue !== newValue) {
            diffRows.push({
              key,
              currentValue: currentValue ?? '',
              newValue: newValue ?? '',
              isNew: currentValue === undefined,
              isRemoved: newValue === undefined
            });
          }
        });

        // Only add section if it has changes
        if (diffRows.length > 0) {
          diffSections.push({
            title: sectionName,
            rows: diffRows
          });
        }
      });

      return diffSections;
    },
    parsedConfigData() {
      if (!this.configRequest?.requested_config) {
        return [];
      }

      const lines = this.configRequest.requested_config.split('\n');
      const sections = [];
      let currentSection = null;

      lines.forEach(line => {
        const trimmedLine = line.trim();

        // Skip empty lines
        if (!trimmedLine) {
          return;
        }

        // Check if it's a section header [SECTION]
        if (trimmedLine.startsWith('[') && trimmedLine.endsWith(']')) {
          // Extract section name and create new section
          const sectionName = trimmedLine.substring(1, trimmedLine.length - 1);
          currentSection = {
            title: sectionName,
            rows: []
          };
          sections.push(currentSection);
        } else {
          // If no current section, create a default one
          if (!currentSection) {
            currentSection = {
              title: 'General',
              rows: []
            };
            sections.push(currentSection);
          }

          // Split on first equals sign to handle values that contain '='
          const equalsIndex = trimmedLine.indexOf('=');
          if (equalsIndex !== -1) {
            const key = trimmedLine.substring(0, equalsIndex).trim();
            const value = trimmedLine.substring(equalsIndex + 1).trim();
            currentSection.rows.push({
              key,
              value
            });
          } else {
            // If no equals sign, treat entire line as key with empty value
            currentSection.rows.push({
              key: trimmedLine,
              value: ''
            });
          }
        }
      });

      return sections;
    }
  },
  methods: {
    async loadConfigRequest() {
      this.loading = true;
      this.notFound = false;
      try {
        const response = await axios.get(`config-change-requests/id/${this.$route.params.id}`);
        this.configRequest = response.data;
      } catch (error) {
        console.error('Failed to load config request:', error);
        if (error.response?.status === 404) {
          this.notFound = true;
        }
      } finally {
        this.loading = false;
      }
    },
    parseConfigToMap(configString) {
      if (!configString) {
        return {};
      }

      const lines = configString.split('\n');
      const sections = {};
      let currentSectionName = 'General';
      sections[currentSectionName] = {};

      lines.forEach(line => {
        const trimmedLine = line.trim();

        // Skip empty lines
        if (!trimmedLine) {
          return;
        }

        // Check if it's a section header [SECTION]
        if (trimmedLine.startsWith('[') && trimmedLine.endsWith(']')) {
          currentSectionName = trimmedLine.substring(1, trimmedLine.length - 1);
          if (!sections[currentSectionName]) {
            sections[currentSectionName] = {};
          }
        } else {
          // Split on first equals sign to handle values that contain '='
          const equalsIndex = trimmedLine.indexOf('=');
          if (equalsIndex !== -1) {
            const key = trimmedLine.substring(0, equalsIndex).trim();
            const value = trimmedLine.substring(equalsIndex + 1).trim();
            sections[currentSectionName][key] = value;
          }
        }
      });

      // Remove empty General section if it was never used
      if (Object.keys(sections['General']).length === 0) {
        delete sections['General'];
      }

      return sections;
    },
    convertMapToSections(configMap, currentMap) {
      const sections = [];

      Object.keys(configMap).forEach(sectionName => {
        const sectionData = configMap[sectionName];
        const rows = Object.entries(sectionData).map(([key, value]) => ({
          key,
          currentValue: currentMap ? (currentMap[sectionName]?.[key] ?? '') : '',
          newValue: value,
          isNew: !currentMap || currentMap[sectionName]?.[key] === undefined,
          isRemoved: false
        }));

        if (rows.length > 0) {
          sections.push({
            title: sectionName,
            rows
          });
        }
      });

      return sections;
    },
    toggleSection(index) {
      this.collapsedSections[index] = !this.collapsedSections[index];
    },
    openConfigInNewWindow(type) {
      const content = type === 'original'
        ? this.configRequest?.current_config
        : this.configRequest?.requested_config;

      if (!content) return;

      const title = type === 'original' ? 'Current Configuration' : 'New Configuration';
      const newWindow = window.open('', '_blank', 'width=800,height=600');

      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${title}</title>
          <style>
            body {
              font-family: monospace;
              padding: 20px;
              background-color: #1e1e1e;
              color: #d4d4d4;
              margin: 0;
            }
            pre {
              white-space: pre-wrap;
              word-wrap: break-word;
              margin: 0;
              line-height: 1.5;
            }
            h1 {
              font-family: system-ui, -apple-system, sans-serif;
              font-size: 18px;
              margin-bottom: 20px;
              color: #fff;
              border-bottom: 1px solid #444;
              padding-bottom: 10px;
            }
          </style>
        </head>
        <body>
          <h1>${title}</h1>
          <pre>${this.escapeHtml(content)}</pre>
        </body>
        </html>
      `);
      newWindow.document.close();
    },
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },
    formatDate(date) {
      if (!date) return '-';
      return dayjs(date).format('DD/MM/YYYY HH:mm');
    },
    getStatusLabel(status) {
      const labels = {
        1: 'Requested',
        2: 'Approved',
        3: 'Rejected',
        4: 'Applied'
      };
      return labels[status] || 'Unknown';
    },
    getStatusSeverity(status) {
      const severities = {
        1: 'info',      // Requested - blue
        2: 'success',   // Approved - green
        3: 'danger',    // Rejected - red
        4: 'secondary'  // Applied - gray
      };
      return severities[status] || 'secondary';
    },
    async approveRequest() {
      this.submitting = true;
      try {
        await axios.patch(`config-change-requests/id/${this.$route.params.id}`, {
          status: 2, // STATUS_APPROVED
        });
        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Config change request approved successfully',
          life: 3000
        });
        this.showApproveModal = false;
        await this.loadConfigRequest();
      } catch (error) {
        console.error('Failed to approve request:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to approve config change request',
          life: 3000
        });
      } finally {
        this.submitting = false;
      }
    },
    async rejectRequest() {
      if (!this.rejectionReason) return;

      this.submitting = true;
      try {
        await axios.patch(`config-change-requests/id/${this.$route.params.id}`, {
          status: 3, // STATUS_REJECTED
          rejected_reason: this.rejectionReason,
        });
        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Config change request rejected',
          life: 3000
        });
        this.showRejectModal = false;
        this.rejectionReason = '';
        await this.loadConfigRequest();
      } catch (error) {
        console.error('Failed to reject request:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to reject config change request',
          life: 3000
        });
      } finally {
        this.submitting = false;
      }
    }
  },
  async mounted() {
    await this.loadConfigRequest();
  }
}
</script>

<style>
thead[role='rowgroup'] > tr > th {
  background-color: #f5f5f5 !important;
}

@media (max-width: 400px) {
  .w-full[style*="max-width: 60%"] {
    max-width: 100% !important;
  }
}
</style>
